import {
  fetchGetUserMember,
  fetchGetUserVirtualCurrencyBalance,
  fetchMaxDiscountMembershipPlan,
  fetchUserInfo,
} from '@/services';
import { MemberState } from '@/types';
import NP from 'number-precision';
import { defineStore } from 'pinia';
import localConf from '@/local-conf';

interface State {
  /** 是否登录 */
  isLogin: boolean;
  /** 未注册 */
  unregistered: boolean;
  /** 用户信息 */
  userInfo: AnyObject | null;
  /** 用户会员信息 */
  userMemberInfo: AnyObject | null;
  /** 最大优惠的会员卡数据 */
  maxDiscountMembershipPlan: AnyObject | null;
  /** 虚拟币余额 */
  virtualCurrencyBalance: number;
}

export const useUserStore = defineStore('user', {
  state: (): State => ({
    isLogin: false,
    unregistered: false,
    userInfo: null,
    userMemberInfo: null,
    maxDiscountMembershipPlan: null,
    virtualCurrencyBalance: 0,
  }),

  getters: {
    /** 是否会员且未过期状态 */
    isMember(state) {
      return !!state.userMemberInfo && state.userMemberInfo.state === MemberState.normal;
    },
    // 联系客服时传入的session-from
    sessionFrom(state) {
      const nickName = state.userInfo?.lastName ?? '微信用户';
      const avatarUrl = state.userInfo?.customFields?.headPortrait ?? '';
      const ysfConfig = JSON.stringify({
        data: JSON.stringify([{ "type": "crm_param", key: 'channel', value: localConf.channel }]),
      });
      return `nickName=${nickName}|foreignID=12345|avatarUrl=${avatarUrl}|ysf.config=${ysfConfig}`;
    },
  },

  actions: {
    async getMaxDiscountMembershipPlan(refresh?: boolean) {
      if (!this.maxDiscountMembershipPlan || refresh) {
        const data = await fetchMaxDiscountMembershipPlan();
        if (data) this.maxDiscountMembershipPlan = data;
      }
      return this.maxDiscountMembershipPlan;
    },
    async getUserInfo(refresh?: boolean) {
      if (!this.userInfo || refresh) {
        const data = await fetchUserInfo();
        this.userInfo = data;
      }
      return this.userInfo;
    },
    async getUserMemberInfo(refresh?: boolean) {
      if (!this.userMemberInfo || refresh) {
        const data = await fetchGetUserMember();
        if (data) this.userMemberInfo = data;
      }
      return this.userMemberInfo;
    },
    // 获取用户虚拟币余额
    async getVirtualCurrencyBalance(refresh?: boolean) {
      if (!this.virtualCurrencyBalance || refresh) {
        const data = await fetchGetUserVirtualCurrencyBalance();
        if (data || data === 0) this.virtualCurrencyBalance = NP.divide(data, 100);
      }
      return this.virtualCurrencyBalance;
    },

    /**
     * 从 UserDataInit 合并查询结果批量设置用户数据
     * 用于场景一优化：减少串行请求
     */
    setFromUserDataInit(data: {
      activeCustomer?: AnyObject | null;
      getUserMember?: AnyObject | null;
      getVirtualCurrencyBalance?: number | null;
    }) {
      // 设置用户信息
      if (data.activeCustomer !== undefined) {
        this.userInfo = data.activeCustomer;
      }

      // 设置会员信息
      if (data.getUserMember !== undefined) {
        this.userMemberInfo = data.getUserMember;
      }

      // 设置虚拟币余额（后端返回的是分，需要转换为元）
      if (data.getVirtualCurrencyBalance !== undefined && data.getVirtualCurrencyBalance !== null) {
        this.virtualCurrencyBalance = NP.divide(data.getVirtualCurrencyBalance, 100);
      }
    },

    /**
     * 从 AppConfigInit 合并查询结果设置最大优惠会员卡数据
     */
    setMaxDiscountMembershipPlanFromInit(data: AnyObject | null) {
      if (data) {
        this.maxDiscountMembershipPlan = data;
      }
    },
  },
});
